import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { STORAGE_KEYS, TokenStorage } from '@/config/storage';
import { API_ROUTES, API_URL } from '@/config/api';
import { tokenSyncService } from './tokenSync';

// Interface para estender o tipo do config do Axios
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
}

// Configurações de refresh token
const REFRESH_CONFIG = {
  // Tempo mínimo antes da expiração para renovar o token (2 minutos)
  MIN_TIME_BEFORE_EXPIRY: 2 * 60 * 1000,
  // Máximo de tentativas de refresh
  MAX_RETRY_ATTEMPTS: 3,
  // Delay entre tentativas (exponencial backoff)
  RETRY_DELAY: 1000,
  // Timeout para requisições de refresh
  REFRESH_TIMEOUT: 10000,
} as const;

let navigationCallback: ((path: string) => void) | null = null;

export const setNavigationCallback = (callback: (path: string) => void) => {
  navigationCallback = callback;
};

// Classe para gerenciar o refresh token de forma centralizada
class TokenRefreshManager {
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string | null, error?: any) => void> = [];
  private retryCount = 0;
  private lastRefreshTime = 0;
  private refreshPromise: Promise<string> | null = null;

  // Verifica se o token está expirado ou próximo de expirar
  isTokenExpiredOrNearExpiry(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      
      // Retorna true se o token já expirou ou está próximo de expirar
      // Usa apenas a margem configurada, sem margem adicional
      return currentTime >= (expirationTime - REFRESH_CONFIG.MIN_TIME_BEFORE_EXPIRY);
    } catch {
      return true;
    }
  }

  // Verifica se o token está completamente expirado
  isTokenFullyExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000;
      return Date.now() >= expirationTime;
    } catch {
      return true;
    }
  }

  // Função para adicionar callbacks à fila de refresh
  addRefreshSubscriber(callback: (token: string | null, error?: any) => void) {
    this.refreshSubscribers.push(callback);
  }

  // Função para executar os callbacks com o novo token
  onRefreshed(token: string) {
    this.refreshSubscribers.forEach((callback) => callback(token));
    this.refreshSubscribers = [];
  }

  // Função para rejeitar todos os callbacks
  rejectRefreshSubscribers(error: any) {
    this.refreshSubscribers.forEach((callback) => callback(null, error));
    this.refreshSubscribers = [];
  }

  // Função principal para fazer refresh do token
  async refreshAccessToken(): Promise<string> {
    // Evita múltiplas requisições simultâneas de refresh
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Verifica se não está tentando fazer refresh muito frequentemente
    const timeSinceLastRefresh = Date.now() - this.lastRefreshTime;
    if (timeSinceLastRefresh < 2000) { // Reduzido para 2 segundos para ser menos restritivo
      console.warn('[TokenRefresh] Refresh muito frequente, aguardando...');
      // Em vez de lançar erro, aguarda um pouco e tenta novamente
      await new Promise(resolve => setTimeout(resolve, 2000 - timeSinceLastRefresh));
    }

    // Verifica se há um processo de login em andamento (evita conflitos)
    if (window.location.pathname === '/login' || window.location.pathname === '/auth') {
      console.warn('[TokenRefresh] Login em andamento, pulando refresh');
      throw new Error('Login em andamento');
    }

    this.refreshPromise = this.performRefresh();
    return this.refreshPromise;
  }

  private async performRefresh(): Promise<string> {
    try {
      this.isRefreshing = true;
      this.lastRefreshTime = Date.now();

      const refreshToken = TokenStorage.getRefreshToken();
      const user = TokenStorage.getUser();
      
      // Verifica se há refresh token e usuário
      if (!refreshToken) {
        throw new Error('Refresh token não disponível');
      }
      
      if (!user) {
        throw new Error('Usuário não encontrado na sessão');
      }

      console.log('[TokenRefresh] Iniciando renovação de token...');

      // Cria uma instância específica para refresh para evitar loops
      const refreshApi = axios.create({
        baseURL: API_URL,
        timeout: REFRESH_CONFIG.REFRESH_TIMEOUT,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const response = await refreshApi.post(API_ROUTES.AUTH.REFRESH_TOKEN, {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token } = response.data;
      
      if (!access_token) {
        throw new Error('Token de acesso não recebido');
      }

      // Salva os novos tokens
      TokenStorage.setTokens(access_token, refresh_token || refreshToken);
      
      // Notifica outras abas sobre a renovação
      tokenSyncService.notifyTokenRefreshed(access_token, refresh_token || refreshToken);
      
      // Notifica os subscribers sobre o sucesso
      this.onRefreshed(access_token);
      
      console.log('[TokenRefresh] Token renovado com sucesso');
      
      return access_token;
    } catch (error: any) {
      console.error('[TokenRefresh] Erro ao renovar token:', error);
      
      // Notifica os subscribers sobre o erro
      this.rejectRefreshSubscribers(error);
      
      // Não limpa a sessão em caso de erro, apenas registra
      console.warn('[TokenRefresh] Mantendo sessão ativa apesar do erro de renovação');
      
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  // Função para aguardar o refresh em andamento
  async waitForRefresh(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.addRefreshSubscriber((token, error) => {
        if (error) {
          reject(error);
        } else if (token) {
          resolve(token);
        } else {
          reject(new Error('Token não recebido'));
        }
      });
    });
  }

  // Verifica se está fazendo refresh
  get isRefreshingToken(): boolean {
    return this.isRefreshing;
  }
}

// Instância global do gerenciador de refresh
export const tokenRefreshManager = new TokenRefreshManager();

// Função para aplicar interceptors em uma instância do axios
function applyAuthInterceptors(axiosInstance: any, instanceName: string) {
  // Interceptor de requisição
  axiosInstance.interceptors.request.use(
    async (config: CustomAxiosRequestConfig) => {
      // Pula a verificação se a requisição não deve usar autenticação
      if (config._skipAuthRefresh) {
        return config;
      }

      const token = TokenStorage.getAccessToken();
      const user = TokenStorage.getUser();
      
      // Se não há token ou usuário, não tenta renovar
      if (!token || !user) {
        return config;
      }

      // Verifica se o token está expirado ou próximo de expirar
      if (tokenRefreshManager.isTokenExpiredOrNearExpiry(token)) {
        // Se o token está completamente expirado, tenta renovar
        if (tokenRefreshManager.isTokenFullyExpired(token)) {
          try {
            if (!tokenRefreshManager.isRefreshingToken) {
              await tokenRefreshManager.refreshAccessToken();
            } else {
              // Aguarda o refresh em andamento
              await tokenRefreshManager.waitForRefresh();
            }
          } catch (error) {
            console.error(`[${instanceName}] Erro ao renovar token:`, error);
            return Promise.reject(error);
          }
        } else {
          // Se está próximo de expirar, renova proativamente apenas ocasionalmente para evitar muitas requisições
          if (!tokenRefreshManager.isRefreshingToken && Math.random() < 0.1) { // 10% de chance
            try {
              await tokenRefreshManager.refreshAccessToken();
            } catch (error) {
              console.warn(`[${instanceName}] Erro na renovação proativa:`, error);
              // Continua com o token atual se a renovação falhar
            }
          }
        }
      }

      // Adiciona o token atualizado
      const currentToken = TokenStorage.getAccessToken();
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      
      return config;
    },
    (error: any) => Promise.reject(error)
  );

  // Interceptor de resposta
  axiosInstance.interceptors.response.use(
    (response: any) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as CustomAxiosRequestConfig;
      
      if (!originalRequest) {
        return Promise.reject(error);
      }

      // Evita loop infinito de refresh
      if (error.response?.status === 401 && originalRequest.url === API_ROUTES.AUTH.REFRESH_TOKEN) {
        console.error(`[${instanceName}] Loop infinito de refresh detectado`);
        console.warn(`[${instanceName}] Mantendo sessão ativa apesar do erro de refresh`);
        // Não limpa a sessão, apenas registra o erro
        return Promise.reject(error);
      }

      // Tenta refresh token apenas para erros 401 e que não sejam do login
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        originalRequest.url !== API_ROUTES.AUTH.LOGIN &&
        !originalRequest._skipAuthRefresh
      ) {
        // Verifica se há usuário autenticado antes de tentar renovar
        const user = TokenStorage.getUser();
        if (!user) {
          console.log(`[${instanceName}] Usuário não autenticado, não tentando renovar token`);
          return Promise.reject(error);
        }
        
        originalRequest._retry = true;

        try {
          let newToken: string;
          
          if (!tokenRefreshManager.isRefreshingToken) {
            newToken = await tokenRefreshManager.refreshAccessToken();
          } else {
            newToken = await tokenRefreshManager.waitForRefresh();
          }
          
          // Atualiza o token na requisição original
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Reexecuta a requisição original
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error(`[${instanceName}] Erro no refresh token:`, refreshError);
          return Promise.reject(refreshError);
        }
      }

      // Tratamento de erros específicos
      if (error.response?.status === 401) {
        console.warn(`[${instanceName}] Erro 401 detectado - mantendo sessão ativa`);
        // Não limpa a sessão automaticamente, apenas registra o erro
        // A sessão será mantida até que o usuário faça logout manualmente
      } else if (error.response?.status === 403) {
        toast.error('Você não tem permissão para realizar esta ação');
      } else if (error.response?.status === 500) {
        toast.error('Erro interno do servidor. Tente novamente mais tarde.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('A requisição demorou muito para responder. Verifique sua conexão.');
      } else if (!error.response) {
        toast.error('Erro de conexão. Verifique sua internet.');
      }

      return Promise.reject(error);
    }
  );
}

// Cria uma instância do axios com configuração base
export const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Cria uma instância do axios específica para upload de arquivos
export const apiUpload = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Cria uma instância do axios específica para downloads de arquivos
export const apiDownload = axios.create({
  baseURL: API_URL,
  timeout: 60000, // Timeout maior para downloads
  responseType: 'blob',
});

// Aplica os interceptors em todas as instâncias
applyAuthInterceptors(api, 'API');
applyAuthInterceptors(apiUpload, 'API_UPLOAD');
applyAuthInterceptors(apiDownload, 'API_DOWNLOAD');

// Função para verificar se o token está expirado (mantida para compatibilidade)
export const isTokenExpired = (token: string): boolean => {
  return tokenRefreshManager.isTokenFullyExpired(token);
};

// Função para fazer refresh do token (mantida para compatibilidade)
export const refreshAccessToken = async (): Promise<string> => {
  return tokenRefreshManager.refreshAccessToken();
}; 